import { Box } from '@mui/material';
import { useLayout } from '@orgnc/core/hooks';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import * as React from 'react';
import NavBar from '@/components/header/scriptmag/NavBar';
import { Logo } from '@orgnc/core/components/atoms';
import SocialList from '@/components/header/SocialList';
import { useEffect, useRef, useState } from 'react';
import GoogleAdTag from '@/components/GoogleAdTag';
import { useAimGeneralOptions } from '../../../utils/useAimGeneralOptions';
import HeaderCard, { HeaderCardProps } from '../HeaderCard';

const CARD_STYLES: HeaderCardProps['sx'] = {};

const Header = () => {
  const { menus, networks, logos } = useLayout();
  const appBarRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const { headerCardsByColumn } = useAimGeneralOptions();

  useEffect(() => {
    setHeaderHeight(appBarRef?.current?.offsetHeight || 0);
  }, [appBarRef]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="relative"
        sx={{
          backgroundColor: 'common.white',
          zIndex: ({ zIndex }) => zIndex.drawer - 1,
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            mt: 1,
            mx: 2,
            display: {
              xs: 'none',
              sm: 'flex',
            },
            a: {
              textDecoration: 'unset',
              color: 'secondary.main',
            },
          }}
        >
          <SocialList networks={networks} size={'small'} />
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            {menus.HEADER_TOP_NAVIGATION?.map(
              (item) =>
                item.path && (
                  <Typography
                    key={`${item.label}.${item.path}`}
                    variant="body1"
                    component="a"
                    href={item.path}
                    target={item.target || ''}
                    rel={item.target ? 'noopener' : undefined}
                    sx={{
                      transition: 'opacity 0.3s ease-in-out',
                      '&:hover': {
                        opacity: '.6',
                      },
                    }}
                  >
                    {item.label}
                  </Typography>
                )
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
            },
            maxWidth: '100%',
            mx: 1,
            pr: 5,
            my: {
              md: 0,
              sm: 0,
            },
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            {headerCardsByColumn?.left.map((card, idx) => (
              <HeaderCard
                key={`left-${idx}`}
                headerCard={card}
                sx={CARD_STYLES}
              />
            ))}
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: {
                md: '450px',
                sm: '40%',
              },
            }}
          >
            {logos.main?.sourceUrl && (
              <Logo
                sourceUrl={logos.main.sourceUrl}
                width={458}
                height={120}
                title={logos.main.altText}
                sx={{
                  maxWidth: {
                    sm: 345,
                    md: 458,
                  },
                  maxHeight: {
                    md: 120,
                    sm: 100,
                  },
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {headerCardsByColumn?.right.map((card, idx) => (
              <HeaderCard
                key={`right-${idx}`}
                headerCard={card}
                sx={CARD_STYLES}
              />
            ))}
          </Box>
        </Box>
        <NavBar
          menu={menus.MAIN_NAVIGATION}
          categoriesMenu={menus.HEADER_NAVIGATION}
          networks={networks}
          additionalMenu={menus.FOOTER_NAVIGATION}
          logo={logos.main || undefined}
          headerHeight={headerHeight}
        />
      </AppBar>
      <GoogleAdTag
        zone="header"
        sizes={[
          [300, 50],
          [300, 100],
          [728, 90],
          [970, 90],
          [970, 250],
        ]}
        mapping={[
          [
            [1024, 768],
            [
              [728, 90],
              [970, 90],
              [970, 250],
            ],
          ],
          [[768, 0], [[728, 90]]],
          [
            [0, 0],
            [
              [300, 50],
              [300, 100],
            ],
          ],
        ]}
        preserveSpace={false}
      />
    </>
  );
};

export default Header;
